import { useContext, useState } from "react";
import { toast } from "react-toastify";
import { MdOutlineCategory } from "react-icons/md";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import useFetchCategory from "../../../marketplace_admin/libs/useFetchcategory";
import useCreateProductCategory from "../../../marketplace_admin/libs/useCreateProductCategory";
import useUpdateProductCategory from "../../../marketplace_admin/libs/useEditProductCategory";
import useDeleteProductCategory from "../../../marketplace_admin/libs/useDeleteProductCategory";
import { AuthContext } from "../../../Context/AuthContext";
import PageHeader from "../../components/PageHeader";
import FilterBar from "../../components/FilterBar";
import DataTable from "../../components/DataTable";
import EmptyState from "../../components/EmptyState";
import ConfirmModal from "../../components/ConfirmModal";
import Spinner from "../../../Spinner/Spinner";

const AdminCategoriesPage = () => {
  const { user } = useContext(AuthContext);
  const { data, isLoading, isError } = useFetchCategory();
  const createMutation = useCreateProductCategory();
  const updateMutation = useUpdateProductCategory();
  const deleteMutation = useDeleteProductCategory();

  const categories = data?.categories || [];
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [formName, setFormName] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const filtered = categories.filter((cat) =>
    (cat.categoryName || "").toLowerCase().includes(search.trim().toLowerCase())
  );

  const openCreate = () => {
    setFormName("");
    setSelectedId(null);
    setModal("create");
  };

  const openEdit = (category) => {
    setSelectedId(category._id);
    setFormName(category.categoryName || "");
    setModal("edit");
  };

  const openDelete = (category) => {
    setSelectedId(category._id);
    setModal("delete");
  };

  const closeModal = () => {
    setModal(null);
    setSelectedId(null);
    setFormName("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const name = formName.trim();
    if (!name) {
      toast.error("Category name is required");
      return;
    }

    try {
      if (modal === "create") {
        await createMutation.mutateAsync({ categoryName: name, creator: user?.userId });
        toast.success("Category created");
      } else {
        await updateMutation.mutateAsync({
          categoryId: selectedId,
          categoryData: { categoryName: name },
        });
        toast.success("Category updated");
      }
      closeModal();
    } catch {
      toast.error("Could not save category");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(selectedId);
      toast.success("Category deleted");
      closeModal();
    } catch {
      toast.error("Could not delete category");
    }
  };

  const columns = [
    {
      key: "name",
      label: "Category",
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.categoryName}</div>
          <div style={{ fontSize: 12, color: "var(--dash-muted)" }}>{row._id?.slice(-8)}</div>
        </div>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="dashboard-table-actions">
          <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => openEdit(row)}>
            <FiEdit2 aria-hidden="true" /> Edit
          </button>
          <button type="button" className="dashboard-btn dashboard-btn--danger" onClick={() => openDelete(row)}>
            <FiTrash2 aria-hidden="true" /> Delete
          </button>
        </div>
      ),
    },
  ];

  const saving = createMutation.isLoading || updateMutation.isLoading;

  return (
    <>
      <PageHeader
        title="Categories"
        subtitle="Manage marketplace product categories merchants can list under"
        icon={MdOutlineCategory}
        actions={
          <button type="button" className="dashboard-btn dashboard-btn--primary" onClick={openCreate}>
            <FiPlus aria-hidden="true" /> Add category
          </button>
        }
      />

      <FilterBar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search categories…" />

      {isLoading && <Spinner />}
      {isError && (
        <div className="dashboard-card">
          <p style={{ color: "var(--dash-danger)", margin: 0 }}>Could not load categories.</p>
        </div>
      )}

      {!isLoading && !isError && filtered.length === 0 && (
        <EmptyState
          icon={MdOutlineCategory}
          title="No categories found"
          message={search ? "Try a different search." : "Add your first category to get started."}
          actionLabel="Add category"
          onAction={openCreate}
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered.map((cat) => ({ ...cat, _key: cat._id }))}
          loading={isLoading}
        />
      )}

      {(modal === "create" || modal === "edit") && (
        <div className="dashboard-modal-backdrop" onClick={closeModal} role="presentation">
          <form className="dashboard-modal" onClick={(e) => e.stopPropagation()} onSubmit={handleSave} role="dialog">
            <h3>{modal === "create" ? "Add category" : "Edit category"}</h3>
            <label className="dashboard-form-field dashboard-form-field--full">
              <span>Category name</span>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Fashion"
                maxLength={80}
                required
                autoFocus
              />
            </label>
            <div className="dashboard-modal__actions">
              <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" className="dashboard-btn dashboard-btn--primary" disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmModal
        open={modal === "delete"}
        title="Delete category?"
        message="Merchants will no longer be able to select this category for new products."
        confirmLabel="Delete"
        danger
        loading={deleteMutation.isLoading}
        onConfirm={handleDelete}
        onCancel={closeModal}
      />
    </>
  );
};

export default AdminCategoriesPage;
